import React from 'react';
import PropTypes from 'prop-types';
import * as Styled from './ProductOptions.styled';

/**
 * A `ProductOptions` is a container which displays a list of options and the selected option.
 */
const ProductOptions = ({
  title,
  name,
  selectedOption,
  onChange,
  children,
  Container,
  minimumOptionsRequired,
  ...props
}) => {
  let displayValue = selectedOption;
  const renderChildren = React.Children.map(children, (child) => {
    const isSelected = child.props.value === selectedOption;
    if (isSelected) {
      displayValue = child.props.label || child.props.value;
    }
    return React.cloneElement(child, {
      isSelected,
      name,
      onChange
    });
  });

  const centralizedTitle = title.toLowerCase();
  const excludedTitles = ['na size'];

  return (
    <fieldset {...props}>
      {!excludedTitles.includes(centralizedTitle) && (
        <Styled.Legend data-testid="productOptionSelectorLegend">
          <Styled.Prefix>{title}:</Styled.Prefix> {displayValue}
        </Styled.Legend>
      )}
      {React.Children.count(children) >= minimumOptionsRequired
        ? Container
          ? React.cloneElement(Container, null, renderChildren)
          : renderChildren
        : null}
    </fieldset>
  );
};

ProductOptions.propTypes = {
  /**
   * The options to render inside the selector.
   */
  children: PropTypes.node,
  /**
   * Prefix to be displayed before the selected option.
   */
  title: PropTypes.string.isRequired,
  /**
   * The name used to reference the value of the control.
   */
  name: PropTypes.string.isRequired,
  /**
   * Value of selected option
   */
  selectedOption: PropTypes.string,
  /**
   * Callback fired when an option is selected.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * optional container for the children, used for SelectTiles
   */
  Container: PropTypes.node,
  /**
   * The minimum number of options we need to display the options
   */
  minimumOptionsRequired: PropTypes.number
};

ProductOptions.defaultProps = {
  children: undefined,
  selectedOption: null,
  Container: undefined,
  minimumOptionsRequired: 2
};

export default ProductOptions;
